import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Card, 
  CardContent,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip
} from '@material-ui/core';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  AreaChart,
  Area,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { useBooking } from '../../contexts/BookingContext';
import { useTrip } from '../../contexts/TripContext';
import { useCompany } from '../../contexts/CompanyContext';

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const FinancialDashboard = () => {
  const { getBookings, getBookingStats } = useBooking();
  const { getTrips } = useTrip();
  const { getCompanies } = useCompany();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('month');
  const [companyFilter, setCompanyFilter] = useState('all');
  
  // Dashboard data
  const [bookings, setBookings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [trips, setTrips] = useState([]);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalCommissions: 0,
    totalRefunds: 0,
    netRevenue: 0,
    averageTicketPrice: 0,
    transactionCount: 0
  });
  
  // Chart data
  const [revenueByDateData, setRevenueByDateData] = useState([]);
  const [revenueByCompanyData, setRevenueByCompanyData] = useState([]);
  const [revenueByRouteData, setRevenueByRouteData] = useState([]);
  const [paymentMethodData, setPaymentMethodData] = useState([]);
  
  // Transactions data
  const [transactions, setTransactions] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  useEffect(() => {
    const fetchFinancialDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all required data
        const allBookings = await getBookings();
        const allTrips = await getTrips();
        const allCompanies = await getCompanies();
        
        setCompanies(allCompanies);
        setTrips(allTrips);
        
        // Filter bookings based on company filter
        let filteredBookings = allBookings;
        if (companyFilter !== 'all') {
          // First get all trips for the selected company
          const companyTrips = allTrips.filter(trip => trip.companyId === companyFilter);
          const companyTripIds = companyTrips.map(trip => trip.id);
          
          // Then filter bookings for those trips
          filteredBookings = allBookings.filter(booking => 
            companyTripIds.includes(booking.tripId)
          );
        }
        
        // Filter bookings based on time range
        const now = new Date();
        let startDate;
        
        switch (timeRange) {
          case 'week':
            startDate = new Date(now);
            startDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            startDate = new Date(now);
            startDate.setMonth(now.getMonth() - 1);
            break;
          case 'year':
            startDate = new Date(now);
            startDate.setFullYear(now.getFullYear() - 1);
            break;
          default:
            startDate = new Date(now);
            startDate.setMonth(now.getMonth() - 1);
        }
        
        filteredBookings = filteredBookings.filter(booking => {
          const bookingDate = new Date(booking.createdAt.seconds * 1000);
          return bookingDate >= startDate;
        });
        
        setBookings(filteredBookings);
        
        // Extract transactions from bookings
        const allTransactions = [];
        
        filteredBookings.forEach(booking => {
          // Add initial payment transaction
          if (booking.paymentStatus === 'completed') {
            allTransactions.push({
              id: `payment-${booking.id}`,
              bookingId: booking.id,
              type: 'payment',
              amount: booking.amount || 0,
              date: booking.paymentDate || booking.createdAt,
              status: 'completed',
              paymentMethod: booking.paymentMethod || 'unknown'
            });
            
            // Add commission transaction
            const commissionAmount = booking.amount * 0.1; // Assuming 10% commission
            allTransactions.push({
              id: `commission-${booking.id}`,
              bookingId: booking.id,
              type: 'commission',
              amount: commissionAmount,
              date: booking.paymentDate || booking.createdAt,
              status: 'completed'
            });
          }
          
          // Add refund transaction if applicable
          if (booking.status === 'cancelled' && booking.refundAmount) {
            allTransactions.push({
              id: `refund-${booking.id}`,
              bookingId: booking.id,
              type: 'refund',
              amount: booking.refundAmount,
              date: booking.cancelledAt || booking.updatedAt || booking.createdAt,
              status: 'completed'
            });
          }
        });
        
        // Sort transactions by date (newest first)
        allTransactions.sort((a, b) => 
          new Date(b.date.seconds * 1000) - new Date(a.date.seconds * 1000)
        );
        
        setTransactions(allTransactions);
        
        // Calculate financial stats
        const totalRevenue = allTransactions
          .filter(t => t.type === 'payment')
          .reduce((sum, t) => sum + t.amount, 0);
          
        const totalCommissions = allTransactions
          .filter(t => t.type === 'commission')
          .reduce((sum, t) => sum + t.amount, 0);
          
        const totalRefunds = allTransactions
          .filter(t => t.type === 'refund')
          .reduce((sum, t) => sum + t.amount, 0);
          
        const netRevenue = totalRevenue - totalRefunds;
        
        const paymentTransactions = allTransactions.filter(t => t.type === 'payment');
        const averageTicketPrice = paymentTransactions.length > 0 
          ? totalRevenue / paymentTransactions.length 
          : 0;
          
        const transactionCount = paymentTransactions.length;
        
        setStats({
          totalRevenue,
          totalCommissions,
          totalRefunds,
          netRevenue,
          averageTicketPrice,
          transactionCount
        });
        
        // Prepare revenue by date data
        const revenueByDate = {};
        const refundsByDate = {};
        
        // Initialize all dates in the range
        let currentDate = new Date(startDate);
        while (currentDate <= now) {
          let dateKey;
          
          if (timeRange === 'week') {
            // Format as day of week (e.g., "Mon")
            dateKey = currentDate.toLocaleDateString('en-US', { weekday: 'short' });
          } else if (timeRange === 'month') {
            // Format as day of month (e.g., "15")
            dateKey = currentDate.getDate().toString();
          } else {
            // Format as month (e.g., "Jan")
            dateKey = currentDate.toLocaleDateString('en-US', { month: 'short' });
          }
          
          if (!revenueByDate[dateKey]) {
            revenueByDate[dateKey] = 0;
            refundsByDate[dateKey] = 0;
          }
          
          // Increment date based on time range
          if (timeRange === 'week' || timeRange === 'month') {
            currentDate.setDate(currentDate.getDate() + 1);
          } else {
            currentDate.setMonth(currentDate.getMonth() + 1);
          }
        }
        
        // Calculate revenue and refunds by date
        allTransactions.forEach(transaction => {
          const transactionDate = new Date(transaction.date.seconds * 1000);
          let dateKey;
          
          if (timeRange === 'week') {
            dateKey = transactionDate.toLocaleDateString('en-US', { weekday: 'short' });
          } else if (timeRange === 'month') {
            dateKey = transactionDate.getDate().toString();
          } else {
            dateKey = transactionDate.toLocaleDateString('en-US', { month: 'short' });
          }
          
          if (revenueByDate[dateKey] !== undefined) {
            if (transaction.type === 'payment') {
              revenueByDate[dateKey] += transaction.amount;
            } else if (transaction.type === 'refund') {
              refundsByDate[dateKey] += transaction.amount;
            }
          }
        });
        
        // Convert to array format for chart
        const revenueByDateArray = Object.keys(revenueByDate).map(date => ({
          date,
          revenue: revenueByDate[date],
          refunds: refundsByDate[date],
          net: revenueByDate[date] - refundsByDate[date]
        }));
        
        setRevenueByDateData(revenueByDateArray);
        
        // Prepare revenue by company data
        const revenueByCompany = {};
        
        allCompanies.forEach(company => {
          revenueByCompany[company.id] = 0;
        });
        
        filteredBookings.forEach(booking => {
          if (booking.status !== 'cancelled' && booking.paymentStatus === 'completed') {
            const trip = allTrips.find(t => t.id === booking.tripId);
            if (trip && trip.companyId) {
              revenueByCompany[trip.companyId] = (revenueByCompany[trip.companyId] || 0) + (booking.amount || 0);
            }
          }
        });
        
        const revenueByCompanyArray = Object.keys(revenueByCompany)
          .filter(companyId => revenueByCompany[companyId] > 0)
          .map(companyId => {
            const company = allCompanies.find(c => c.id === companyId);
            return {
              name: company ? company.name : 'Unknown',
              value: revenueByCompany[companyId]
            };
          })
          .sort((a, b) => b.value - a.value);
        
        setRevenueByCompanyData(revenueByCompanyArray);
        
        // Prepare revenue by route data
        const revenueByRoute = {};
        
        filteredBookings.forEach(booking => {
          if (booking.status !== 'cancelled' && booking.paymentStatus === 'completed') {
            const trip = allTrips.find(t => t.id === booking.tripId);
            if (trip) {
              const route = `${trip.origin} - ${trip.destination}`;
              revenueByRoute[route] = (revenueByRoute[route] || 0) + (booking.amount || 0);
            }
          }
        });
        
        const revenueByRouteArray = Object.keys(revenueByRoute)
          .map(route => ({
            name: route,
            value: revenueByRoute[route]
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 10); // Top 10 routes by revenue
        
        setRevenueByRouteData(revenueByRouteArray);
        
        // Prepare payment method data
        const paymentMethods = {};
        
        paymentTransactions.forEach(transaction => {
          const method = transaction.paymentMethod || 'unknown';
          paymentMethods[method] = (paymentMethods[method] || 0) + transaction.amount;
        });
        
        const paymentMethodArray = Object.keys(paymentMethods).map(method => ({
          name: method.charAt(0).toUpperCase() + method.slice(1), // Capitalize first letter
          value: paymentMethods[method]
        }));
        
        setPaymentMethodData(paymentMethodArray);
        
      } catch (error) {
        console.error('Error fetching financial dashboard data:', error);
        setError('Failed to load financial dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFinancialDashboardData();
  }, [timeRange, companyFilter]);
  
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };
  
  const handleCompanyFilterChange = (event) => {
    setCompanyFilter(event.target.value);
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Helper function to format currency
  const formatCurrency = (amount) => {
    return `$${amount.toFixed(2)}`;
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Financial Dashboard
      </Typography>
      
      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            onChange={handleTimeRangeChange}
            label="Time Range"
          >
            <MenuItem value="week">Last Week</MenuItem>
            <MenuItem value="month">Last Month</MenuItem>
            <MenuItem value="year">Last Year</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Company</InputLabel>
          <Select
            value={companyFilter}
            onChange={handleCompanyFilterChange}
            label="Company"
          >
            <MenuItem value="all">All Companies</MenuItem>
            {companies.map(company => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Revenue
              </Typography>
              <Typography variant="h4">
                {formatCurrency(stats.totalRevenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Net Revenue
              </Typography>
              <Typography variant="h4">
                {formatCurrency(stats.netRevenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Commissions
              </Typography>
              <Typography variant="h4">
                {formatCurrency(stats.totalCommissions)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Refunds
              </Typography>
              <Typography variant="h4">
                {formatCurrency(stats.totalRefunds)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg. Ticket
              </Typography>
              <Typography variant="h4">
                {formatCurrency(stats.averageTicketPrice)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Transactions
              </Typography>
              <Typography variant="h4">
                {stats.transactionCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Charts */}
      <Grid container spacing={3}>
        {/* Revenue Trend Chart */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 400,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Revenue Trends
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={revenueByDateData}
                margin={{
                  top: 10,
                  right: 30,
                  left: 0,
                  bottom: 0,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="revenue" 
                  stackId="1"
                  stroke="#8884d8" 
                  fill="#8884d8" 
                  name="Revenue"
                />
                <Area 
                  type="monotone" 
                  dataKey="refunds" 
                  stackId="2"
                  stroke="#FF8042" 
                  fill="#FF8042" 
                  name="Refunds"
                />
                <Line 
                  type="monotone" 
                  dataKey="net" 
                  stroke="#82ca9d" 
                  name="Net Revenue"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Revenue by Company Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Revenue by Company
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={revenueByCompanyData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {revenueByCompanyData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Payment Methods Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Revenue by Payment Method
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={paymentMethodData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {paymentMethodData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Revenue by Route Chart */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Top Routes by Revenue
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={revenueByRouteData}
                layout="vertical"
                margin={{
                  top: 5,
                  right: 30,
                  left: 100,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="value" name="Revenue" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Transactions Table */}
      <Paper sx={{ width: '100%', mt: 3 }}>
        <Typography variant="h6" sx={{ p: 2 }}>
          Recent Transactions
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Transaction ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment Method</TableCell>
                <TableCell>Booking ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>{transaction.id.substring(0, 8)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={transaction.type} 
                        color={
                          transaction.type === 'payment' ? 'success' : 
                          transaction.type === 'refund' ? 'error' : 
                          'primary'
                        } 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>{formatCurrency(transaction.amount)}</TableCell>
                    <TableCell>
                      {transaction.date 
                        ? new Date(transaction.date.seconds * 1000).toLocaleString() 
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={transaction.status} 
                        color={transaction.status === 'completed' ? 'success' : 'warning'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>{transaction.paymentMethod || 'N/A'}</TableCell>
                    <TableCell>{transaction.bookingId.substring(0, 8)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={transactions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Container>
  );
};

export default FinancialDashboard;
